import { useQuery } from '@tanstack/react-query';
import {
  AdminUsersApi,
  type AdminUserSearchResultDto,
} from '@up2stay/up2promo-api-client';
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  Button,
  Combobox,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
  ComboboxTrigger,
  cn,
} from '@up2stay/up2promo-ui';
import { useState } from 'react';

import { useDebounce } from '@/core/hooks/use-debounce';
import {
  adminUserAvatarUrl,
  adminUserInitials,
} from '@/core/lib/admin-user-display';
import { triggerSelectionHaptic } from '@/core/lib/dnd-haptics';

function userLabel(user: AdminUserSearchResultDto): string {
  const name = user.displayName?.trim();
  if (name) return name;
  return user.email ?? user.username ?? user.id;
}

function userSecondary(user: AdminUserSearchResultDto): string | null {
  const parts: string[] = [];
  if (user.email?.trim()) {
    parts.push(user.email.trim());
  }
  if (user.username?.trim()) {
    parts.push(`@${user.username.trim()}`);
  }
  if (user.workspaceNames.length > 0) {
    parts.push(user.workspaceNames.join(', '));
  }
  return parts.length > 0 ? parts.join(' · ') : null;
}

function UserAvatar({
  user,
  className,
}: {
  user: AdminUserSearchResultDto;
  className?: string;
}) {
  const src = adminUserAvatarUrl(user);
  const initials = adminUserInitials(user);
  return (
    <Avatar className={cn('size-8 shrink-0', className)}>
      {src ? <AvatarImage src={src} alt="" /> : null}
      <AvatarFallback className="text-[10px] font-medium">
        {initials}
      </AvatarFallback>
    </Avatar>
  );
}

function UserRowContent({ user }: { user: AdminUserSearchResultDto }) {
  const label = userLabel(user);
  const secondary = userSecondary(user);
  return (
    <div className="flex min-w-0 flex-1 items-center gap-2">
      <UserAvatar user={user} />
      <span className="min-w-0 flex-1">
        <span className="block truncate text-sm font-medium">{label}</span>
        {secondary ? (
          <span className="block truncate text-xs text-muted-foreground">
            {secondary}
          </span>
        ) : null}
      </span>
    </div>
  );
}

export type SupportImpersonateComboboxProps = {
  selectedUser: AdminUserSearchResultDto | null;
  onSelect: (user: AdminUserSearchResultDto) => void;
  disabled?: boolean;
  className?: string;
  placeholder: string;
  searchPlaceholder: string;
  emptyMessage: string;
  loadingMessage: string;
  locale: string;
};

export function SupportImpersonateCombobox({
  selectedUser,
  onSelect,
  disabled,
  className,
  placeholder,
  searchPlaceholder,
  emptyMessage,
  loadingMessage,
  locale,
}: SupportImpersonateComboboxProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 300);

  const trimmedSearch = debouncedSearch.trim();
  const canSearch = trimmedSearch.length >= 2;

  const {
    data: results = [],
    isFetching,
    isError,
  } = useQuery({
    queryKey: ['admin-user-search', trimmedSearch] as const,
    queryFn: async () => {
      const response = await AdminUsersApi.searchAdminUsers({
        query: {
          q: trimmedSearch,
          limit: 20,
        },
      });
      return response.data ?? [];
    },
    enabled: open && canSearch,
    staleTime: 30 * 1000,
    retry: 1,
  });

  const displayLabel = selectedUser ? userLabel(selectedUser) : placeholder;

  return (
    <Combobox
      items={results}
      value={selectedUser}
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) setSearch('');
      }}
      onValueChange={(next) => {
        if (!next) return;
        void triggerSelectionHaptic();
        onSelect(next);
        setSearch('');
      }}
      onInputValueChange={(next, { reason }) => {
        if (reason === 'item-press') return;
        setSearch(next);
      }}
      filter={null}
      itemToStringLabel={userLabel}
      isItemEqualToValue={(a, b) => a.id === b.id}
      disabled={disabled}
      inputValue={search}
    >
      <div className={cn('w-full', className)}>
        <ComboboxTrigger
          render={
            <Button
              type="button"
              variant="outline"
              size="lg"
              className={cn(
                'w-full justify-between gap-2 px-3 font-normal',
                !selectedUser && 'text-muted-foreground',
              )}
            />
          }
        >
          <span className="flex min-w-0 flex-1 items-center gap-2 overflow-hidden text-left">
            {selectedUser ? (
              <UserAvatar user={selectedUser} className="size-5" />
            ) : null}
            <span className="min-w-0 flex-1 truncate text-sm">
              {displayLabel}
            </span>
          </span>
        </ComboboxTrigger>
      </div>
      <ComboboxContent align="start" sideOffset={4}>
        <ComboboxInput placeholder={searchPlaceholder} showTrigger={false} />
        {!canSearch ? (
          <div className="px-3 py-6 text-left text-sm text-muted-foreground">
            {searchPlaceholder}
          </div>
        ) : null}
        {canSearch && isError ? (
          <div className="px-3 py-6 text-left text-sm text-destructive">
            {locale === 'de' ? 'Suche fehlgeschlagen.' : 'Search failed.'}
          </div>
        ) : null}
        {canSearch && isFetching && results.length === 0 ? (
          <div className="px-3 py-6 text-left text-sm text-muted-foreground">
            {loadingMessage}
          </div>
        ) : null}
        <ComboboxEmpty>
          {canSearch && !isFetching && !isError ? emptyMessage : null}
        </ComboboxEmpty>
        <ComboboxList>
          {(user: AdminUserSearchResultDto) => (
            <ComboboxItem key={user.id} value={user} className="gap-2 px-2">
              <UserRowContent user={user} />
            </ComboboxItem>
          )}
        </ComboboxList>
      </ComboboxContent>
    </Combobox>
  );
}
